import React from 'react';
import { FetchBaseQueryError, FetchBaseQueryMeta, QueryReturnValue } from '@reduxjs/toolkit/query';
import { AppError, AuthServiceError, ProblemDetails } from '@/types/api.def.ts';
import { Button } from 'antd';
import { CopyButton } from '@/components/CopyButton.tsx';
import { notification } from '@/utils/antdAppFeedback.ts';

type NotificationErrorConfig = Parameters<typeof notification.error>[0];

const openErrorNotification = (config: NotificationErrorConfig) => {
  notification.error(config);
};

/**
 * Type predicate to narrow an unknown error to `FetchBaseQueryError`
 */
export const isFetchBaseQueryError = (error: unknown): error is FetchBaseQueryError => {
  return typeof error === 'object' && error != null && 'status' in error;
};

export const isAppError = (obj: unknown): obj is AppError => {
  return (
    typeof obj === 'object' &&
    obj != null &&
    'errorCode' in obj &&
    'message' in obj &&
    'details' in obj
  );
};

/**
 * Hand-constructed backend ProblemDetails responses can omit `type` and `traceId`, while still
 * carrying useful title, status, and detail values.
 */
export const isProblemDetails = (obj: unknown): obj is ProblemDetails => {
  return typeof obj === 'object' && obj != null && 'title' in obj && 'status' in obj;
};

/**
 * Builds a React node describing the ProblemDetails error for display in a
 * notification.  Handles both:
 *  - Domain errors (detail string, no errors dict)
 *  - ASP.NET validation errors (errors dict, no detail)
 */
export const formatProblemDetailsDescription = (problem: ProblemDetails): React.ReactNode => {
  const parts: React.ReactNode[] = [];

  if (problem.detail) {
    parts.push(
      <p key="detail" className="m-0">
        {problem.detail}
      </p>,
    );
  }

  if (problem.errors && Object.keys(problem.errors).length > 0) {
    const items = Object.entries(problem.errors).flatMap(([field, messages]) =>
      messages.map((msg, i) => (
        <li key={`${field}-${i}`}>
          <strong>{field}</strong>: {msg}
        </li>
      )),
    );
    parts.push(
      <ul key="errors" className="m-0 pl-4">
        {items}
      </ul>,
    );
  }

  if (parts.length === 0) {
    return problem.title;
  }

  return <>{parts}</>;
};

export const getApiErrorDescription = (
  error: unknown,
  fallback: React.ReactNode = 'Please refresh and try again.',
): React.ReactNode => {
  if (isFetchBaseQueryError(error)) {
    if (isProblemDetails(error.data)) {
      return formatProblemDetailsDescription(error.data);
    }

    if (isAuthServiceError(error.data) || isAppError(error.data)) {
      return error.data.message;
    }

    if ('error' in error && typeof error.error === 'string') {
      return error.error;
    }

    if (typeof error.status === 'number') {
      return `HTTP ${error.status} error`;
    }
  }

  if (
    typeof error === 'object' &&
    error != null &&
    'message' in error &&
    typeof error.message === 'string'
  ) {
    return error.message;
  }

  return fallback;
};

const isAuthServiceError = (obj: unknown): obj is AuthServiceError => {
  return (
    typeof obj === 'object' &&
    obj != null &&
    'timestamp' in obj &&
    'message' in obj &&
    'valid' in obj &&
    'statusCode' in obj
  );
};

export const handleApiError = (error: unknown, message: string = '') => {
  if (isFetchBaseQueryError(error)) {
    if (isProblemDetails(error.data)) {
      openErrorNotification({
        title: message || error.data.title,
        description: formatProblemDetailsDescription(error.data),
        actions: <CopyButton copyData={error.data}>Copy Error</CopyButton>,
      });
    } else if (isAuthServiceError(error.data)) {
      openErrorNotification({
        title: message,
        description: error.data.message,
        actions: <CopyButton copyData={error.data}>Copy Error</CopyButton>,
      });
    } else if (isAppError(error.data)) {
      openErrorNotification({
        title: message,
        description: error.data.message,
        actions: <CopyButton copyData={error.data}>Copy Error</CopyButton>,
      });
    } else {
      // Unknown error shape — show status + raw body so the user always sees something
      const status = typeof error.status === 'number' ? error.status : 'Unknown';
      openErrorNotification({
        title: message || 'Request Failed',
        description: `HTTP ${status} error`,
        actions: (
          <CopyButton copyData={(error.data as object) ?? { status }}>Copy Error</CopyButton>
        ),
      });
    }
  }
};

const isAuthorizationDenied = (data: unknown): boolean => {
  if (typeof data !== 'object' || data == null || !('message' in data)) return false;

  const { message } = data as { message?: unknown };
  return (
    typeof message === 'string' &&
    (message.includes('does not have permission for the specified function') ||
      message.includes('do not have any role with access rights'))
  );
};

export const handleSessionExpiredError = (
  result: QueryReturnValue<unknown, FetchBaseQueryError, FetchBaseQueryMeta>,
) => {
  const isExpired =
    result.error?.status === 302 ||
    (result.error?.status === 401 && !isAuthorizationDenied(result.error.data));

  if (isExpired) {
    openErrorNotification({
      key: 'session-expired',
      title: 'Session Expired',
      description:
        'Your session has expired. Please login again. Please save your unsaved work before refreshing the page.',
      duration: undefined,
      actions: (
        <Button
          danger
          onClick={() => {
            window.location.reload();
          }}
        >
          Refresh
        </Button>
      ),
    });
  }
};
